import { useEffect, useMemo } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import UnderlineExtension from '@tiptap/extension-underline'
import { TextStyle } from '@tiptap/extension-text-style'
import { marked } from 'marked'
import { EditorToolbar } from './EditorToolbar'

interface RichTextEditorProps {
  content: string
  onChange: (html: string) => void
  templateId?: string
}

function isMarkdown(text: string): boolean {
  return /(\*\*|__|##|^\s*[-*]\s)/m.test(text)
}

function toHtml(content: string): string {
  if (!content) return ''
  if (isMarkdown(content)) {
    return marked.parse(content, { async: false }) as string
  }
  return content
}

export function RichTextEditor({
  content,
  onChange,
  templateId,
}: RichTextEditorProps) {
  const htmlContent = useMemo(() => toHtml(content), [content])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      UnderlineExtension,
      TextStyle,
    ],
    content: htmlContent,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none min-h-[400px] p-4 focus:outline-none',
      },
    },
  })

  // Sync content when template changes
  useEffect(() => {
    if (editor && htmlContent !== editor.getHTML()) {
      editor.commands.setContent(htmlContent)
    }
  }, [templateId, htmlContent, editor])

  return (
    <div className="rounded-md border bg-background">
      <EditorToolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  )
}
